import React, { useState, useLayoutEffect, useCallback } from "react";
import { Switch, Route, RouteComponentProps } from "react-router";
import LogIn from "./LogIn/LogIn";
import QuickLogin from "./QuickLogin/QuickLogin";
import { Grid, Paper, Typography } from "@material-ui/core";
import LoginTabs from "./LoginTabs/LoginTabs";
import { MobXProviderContext } from "mobx-react";
import useStyles, { STGrid, ForgotPasswordLink, OrganizationGrid, OrganizationText } from "./styles";
import { ThemeProvider } from "styled-components";
import { Toast } from "@src/Tools/Toast";
import { Values } from "@src/Tools/Values";
import { loginRoute, oauthLogInRoute, oauthLogInErrorRoute, oauthLogInByPinRoute, oauthLogin2FARoute } from "@src/Stores/Auth/types";
import LowPriorityIcon from "@material-ui/icons/LowPriority";
import LoginWithRequiredProvider from "./LoginWithRequiredProvider/LoginWithRequiredProvider";
import Login2FA from "@src/Components/Pages/Authorization/Login2FA/Login2FA";

function useStores() {
  return React.useContext(MobXProviderContext);
}

const Authorization = (props: RouteComponentProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const { auth, values, routing, themes, management } = useStores();
  const classes = useStyles({});
  const [, updateState] = useState();
  const forceUpdate = useCallback(() => updateState({}), []);

  let UseOAuthRoutes = false;

    const refreshNonceAndCorrelationBySignInPageReopen = useCallback(() => {
        const currentTime = new Date().getTime();
        const { search } = props.location;
        const returnUrl = new URLSearchParams(search).get("clientAppUrl");

        if (returnUrl) {
            localStorage.setItem('lastSignInPageRefreshTime', currentTime.toString());
            window.location.href = decodeURIComponent(returnUrl);
        }
    }, [props.location]);


    const handleVisibilityChange = useCallback(() => {
        const refreshInterval = 10 * 60 * 1000;

        if (isPageVisible()) {
            const currentTime = new Date().getTime();
            const lastRefreshTime = localStorage.getItem('lastSignInPageRefreshTime') || 0;
            const timeSinceLastRefresh = currentTime - Number(lastRefreshTime);
            if (timeSinceLastRefresh >= refreshInterval) {
                refreshNonceAndCorrelationBySignInPageReopen();
            }
        }
    }, [refreshNonceAndCorrelationBySignInPageReopen]);

  
    function isPageVisible() {
        return !document.hidden;
    }

    useLayoutEffect(() => {
    // 10 minute refresh interval is added to refresh nonce and correlation cookies required by OIDC flow
    const signInPageRefreshTimer = setInterval(refreshNonceAndCorrelationBySignInPageReopen, 600000); 
    // Add event listener to cover cases when pc was in sleep mode.
    document.addEventListener('visibilitychange', handleVisibilityChange);

    async function componentDidMount() {
      const allowRedirectToLogin = () => {
        const { search, pathname } = props.location;
        var returnUrl = new URLSearchParams(search).get("returnUrl");
        var isLoginRoute = pathname.match(loginRoute);
        
        return isLoginRoute && !!returnUrl;
      };

        const tenantParam = new URLSearchParams(props.location.search).get("tenant");
        if (props.location.pathname.match(oauthLogInRoute) || props.location.pathname.match(oauthLogInErrorRoute)) {
            UseOAuthRoutes = true;
            if (routing.location.pathname.indexOf("AuthorizeError") !== -1) {
                const toast = new Toast();
                const message = new URLSearchParams(routing.location.search).get("message");
                toast.showError(message);
                await values.getLocationsForLogin();
            }
        } else {
            if (values.requireInitialSynchronization) {
                routing.history.push("/synchronization");
                values.requireInitialSynchronization = false;
            }
            if (auth.isAuthorized && !UseOAuthRoutes && !allowRedirectToLogin()) {
                routing.history.push("/");
            }
        }
        if (routing.location.search.indexOf("authorizeFailed=true") !== -1) {
            const toast = new Toast();
            const message = new URLSearchParams(routing.location.search).get("message");
            toast.showError(message ? message : Values.getLocalizationValue("authorizationFailed"));
        }
        await values.getLocationsForLogin(tenantParam);
        sessionStorage.removeItem("InvalidCredentials");
        await themes.getCurrentTheme(values.themeTypes.Login, tenantParam);
        await management.getShortAuthProviders(tenantParam);

        setIsLoading(false);
    }
    if (isLoading) {
      componentDidMount();
    }

    return () => {
        clearInterval(signInPageRefreshTimer);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    }, [refreshNonceAndCorrelationBySignInPageReopen, handleVisibilityChange]);

  if (isLoading) {
    return null;
  }

  return (
    <ThemeProvider theme={themes.login}>
      <STGrid
        id="login-page"
        container
        justify="center"
        direction="column"
        alignItems="center"
        className={classes.loginGrid}
        loginbackgroundlandscapeimageurl={themes.login.loginThemeDetails.loginBackgroundLandscapeImageUrl}
        loginbackgroundportraitimageurl={themes.login.loginThemeDetails.loginBackgroundPortraitImageUrl}
        styles={themes.login.css}
      >
        <img
          id="login-page-logo"
          className={classes.loginLogo}
          src={themes.login.loginThemeDetails.loginLogoImageUrl}
        />
        <Paper elevation={2} className={classes.paperRoot} id="login-form">
          <OrganizationGrid item xs={12}>
            <OrganizationText
                href={`/workspace/${values.tenant}`}
                >
                {values.defaultSeededOrganizationName}
            </OrganizationText>
            <LowPriorityIcon
              style={{ cursor: "pointer" }}
              onClick={() => {
                  if (values.launcherUrl) window.location.href = values.launcherUrl + '#workspace';
              }}
            />
          </OrganizationGrid>
          {!management.shortRequiredAuthProviders && !props.history.location.pathname.endsWith(`${props.match.url}/2fa`) && (
                <Grid item>
                    <LoginTabs/>
                </Grid>
          )}
          <Grid item>
          <>
              { management.shortRequiredAuthProviders && (
                  <Switch>
                      <Route
                          path={UseOAuthRoutes ? oauthLogInRoute : `${props.match.url}`}
                          component={(props: any) => <LoginWithRequiredProvider {...props} forceUpdate={forceUpdate} />}
                      />
                  </Switch>
              )}
              { !management.shortRequiredAuthProviders && (
                  <Switch>
                    <Route path={
                        UseOAuthRoutes ? oauthLogInByPinRoute : `${props.match.url}/pin`} exact component={QuickLogin} />
                    <Route path={UseOAuthRoutes ? oauthLogin2FARoute : `${props.match.url}/2fa`} exact component={Login2FA} />
                    <Route
                        path={UseOAuthRoutes ? oauthLogInRoute : `${props.match.url}`}
                        component={(props: any) => <LogIn {...props} forceUpdate={forceUpdate} />}
                    />
                  </Switch>
              )}
          </>
          </Grid>
        </Paper>
        {values.isEmailSettingsConfigured && (
          <ForgotPasswordLink
            id="forgot-password"
            href="/forgot-password"
            style={
              sessionStorage.getItem("InvalidCredentials") ? { color: "#0052cc" } : { color: "rgba(0, 0, 0, 0.54)" }
            }
          >
            <Typography variant="body2">{Values.getLocalizationValue("forgotPassword")}</Typography>
          </ForgotPasswordLink>
        )}
      </STGrid>
    </ThemeProvider>
  );
};

export default Authorization;
